import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Checkbox, Input, SkeletonText, Textarea, useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import type { ChannelDetail, SearchablePerson } from "./types.js";

/* ──────────────────────────────────── edit channel ──────────────────────────────────── */

interface EditChannelProps {
  channel: ChannelDetail["channel"];
  onSaved: (changes: { name: string; topic: string | null }) => void;
  onClose: () => void;
}

export function EditChannelDialog({ channel, onSaved, onClose }: EditChannelProps) {
  const { toast } = useToast();
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic ?? "");
  const [saving, setSaving] = useState(false);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    const nextTopic = topic.trim() || null;

    api
      .patch(`/channels/${channel.id}`, { name: trimmed, topic: nextTopic })
      .then(() => {
        onSaved({ name: trimmed, topic: nextTopic });
        onClose();
        toast("Channel updated.");
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not save those changes.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [name, topic, channel.id, onSaved, onClose, toast]);

  return (
    <form
      className="chat-edit"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input label="Channel name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />

      <Textarea
        label="Topic"
        hint="One line on what belongs here. Shown under the channel name."
        value={topic}
        maxRows={3}
        onChange={(e) => setTopic(e.target.value)}
      />

      <div className="chat-edit__actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!name.trim() || saving}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

/* ──────────────────────────────────── add members ──────────────────────────────────── */

/**
 * Choose people to add to a channel.
 *
 * Anyone already in it is shown as such rather than hidden: "why is Divya not
 * in this list" is a worse question than seeing her greyed out and labelled.
 */
export function AddMembersDialog({
  existingIds,
  onClose,
  onAdd,
}: {
  existingIds: Set<string>;
  onClose: () => void;
  onAdd: (userIds: string[]) => Promise<void>;
}) {
  const { toast } = useToast();
  const [people, setPeople] = useState<SearchablePerson[]>([]);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ people: SearchablePerson[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load people.", { tone: "danger" });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter(
      (person) =>
        person.displayName.toLowerCase().includes(q) ||
        (person.jobTitle ?? "").toLowerCase().includes(q) ||
        (person.department ?? "").toLowerCase().includes(q),
    );
  }, [people, query]);

  const toggle = (id: string) => {
    setChosen((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="chat-add-members">
      <Input
        placeholder="Search by name or email"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      <div className="chat-add-members__list">
        {loading ? (
          <SkeletonText lines={5} />
        ) : filtered.length === 0 ? (
          <p className="chat-add-members__empty">Nobody matches.</p>
        ) : (
          filtered.map((person) => {
            const already = existingIds.has(person.id);
            return (
              <label
                key={person.id}
                className={`chat-add-members__row ${already ? "is-member" : ""}`}
              >
                <Checkbox
                  checked={already || chosen.has(person.id)}
                  disabled={already}
                  onChange={() => toggle(person.id)}
                  aria-label={`Add ${person.displayName}`}
                />
                <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={26} />
                <span className="chat-add-members__name">
                  <strong>{person.displayName}</strong>
                  <span>{already ? "Already here" : (person.jobTitle ?? person.department ?? "")}</span>
                </span>
              </label>
            );
          })
        )}
      </div>

      <div className="chat-add-members__actions">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          disabled={chosen.size === 0 || saving}
          loading={saving}
          onClick={() => {
            setSaving(true);
            void onAdd([...chosen]).finally(() => setSaving(false));
          }}
        >
          {chosen.size === 0 ? "Add people" : `Add ${chosen.size}`}
        </Button>
      </div>
    </div>
  );
}
